/**
 * Global refresh token manager to prevent concurrent refresh attempts
 * This singleton ensures only one refresh operation happens at a time
 * and proactively refreshes tokens before they expire
 */

import { getTokenExpiry } from './tokenUtils';

export interface RefreshResult {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: unknown;
}

type RefreshFunction = (refreshToken: string) => Promise<RefreshResult>;

class RefreshTokenManager {
  private refreshPromise: Promise<RefreshResult> | null = null;
  private refreshFunction: RefreshFunction | null = null;
  private lastRefreshTime: number = 0;
  private readonly MIN_REFRESH_INTERVAL = 5000; // 5 seconds minimum between refreshes

  // Proactive refresh timer
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

  /**
   * Set the refresh function implementation (called by AuthContext)
   */
  setRefreshFunction(fn: RefreshFunction): void {
    this.refreshFunction = fn;
  }

  /**
   * Perform a token refresh with concurrency protection
   */
  async refreshToken(): Promise<RefreshResult> {
    // If already refreshing, return the existing promise
    if (this.refreshPromise) {
      console.log('Refresh already in progress, waiting for completion...');
      return this.refreshPromise;
    }

    // Prevent rapid successive refreshes
    const now = Date.now();
    if (now - this.lastRefreshTime < this.MIN_REFRESH_INTERVAL) {
      console.log('Refresh attempted too soon, skipping...');
      return { success: false };
    }

    if (!this.refreshFunction) {
      console.error('Refresh function not set');
      return { success: false };
    }

    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      console.log('No refresh token available');
      return { success: false };
    }

    console.log('Starting token refresh...');

    // Create and store the refresh promise
    this.refreshPromise = this.performRefresh(storedRefreshToken);

    try {
      const result = await this.refreshPromise;
      this.lastRefreshTime = Date.now();
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(refreshToken: string): Promise<RefreshResult> {
    try {
      if (!this.refreshFunction) {
        throw new Error('Refresh function not available');
      }

      const result = await this.refreshFunction(refreshToken);

      if (result.success) {
        console.log('Token refresh successful');
      } else {
        console.log('Token refresh failed');
      }

      return result;
    } catch (error) {
      console.error('Token refresh error:', error);
      return { success: false };
    }
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshPromise !== null;
  }

  /**
   * Clear any pending refresh (useful for cleanup)
   */
  clear(): void {
    this.refreshPromise = null;
  }

  /**
   * Start proactive refresh monitoring
   * Schedules a token refresh before the current token expires
   */
  startProactiveRefresh(token: string): void {
    this.stopProactiveRefresh(); // Clear any existing timer

    const expiry = getTokenExpiry(token);
    if (!expiry) {
      console.log('Cannot schedule proactive refresh: unable to determine token expiry');
      return;
    }

    const now = Date.now();
    const expiryTime = expiry.getTime();
    const refreshAt = expiryTime - this.REFRESH_BUFFER_MS;
    const delay = Math.max(refreshAt - now, 0);

    if (delay > 0) {
      const delayMinutes = Math.round(delay / 1000 / 60);
      console.log(`Proactive refresh scheduled in ${delayMinutes} minutes`);
      this.refreshTimer = setTimeout(() => this.executeProactiveRefresh(), delay);
    } else {
      // Token already near expiry, refresh now
      console.log('Token near expiry, refreshing immediately');
      this.executeProactiveRefresh();
    }
  }

  /**
   * Stop proactive refresh monitoring
   * Should be called on logout or session expiry
   */
  stopProactiveRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      console.log('Proactive refresh timer stopped');
    }
  }

  /**
   * Execute proactive refresh and schedule next one.
   * Includes a guard to prevent double-refresh if the visibilitychange handler
   * already refreshed the token while this timer was suspended.
   */
  private async executeProactiveRefresh(): Promise<void> {
    console.log('Proactive token refresh triggered');

    // Guard: check if token was already refreshed (e.g., by visibilitychange handler)
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      const expiry = getTokenExpiry(currentToken);
      if (expiry) {
        const timeUntilExpiry = expiry.getTime() - Date.now();
        if (timeUntilExpiry > this.REFRESH_BUFFER_MS) {
          console.log('Token already refreshed, rescheduling proactive refresh');
          this.startProactiveRefresh(currentToken);
          return;
        }
      }
    }

    const result = await this.refreshToken();

    if (result.success && result.token) {
      // Schedule next refresh based on new token
      this.startProactiveRefresh(result.token);
    } else {
      console.log('Proactive refresh failed, will rely on reactive refresh');
    }
  }
}

// Export singleton instance
export const refreshTokenManager = new RefreshTokenManager();